import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle, GlassCardDescription } from "@/components/ui/glass-card";
import CustomerTag from "@/components/CustomerTag";

export function DashboardDataTable() {
  // Combine all the data from different charts
  const departmentData = [
    { name: "Engineering", devices: 24, color: '#6366F1' },
    { name: "Marketing", devices: 12, color: '#A5B4FC' },
    { name: "Sales", devices: 18, color: '#818CF8' },
    { name: "Finance", devices: 9, color: '#4F46E5' },
    { name: "HR", devices: 7, color: '#4338CA' },
  ];

  const locationData = [
    { name: "Cupertino HQ", devices: 32, color: '#EA580C' },
    { name: "New York Office", devices: 14, color: '#F97316' },
    { name: "Remote", devices: 8, color: '#FB923C' },
    { name: "London Office", devices: 11, color: '#FDBA74' },
    { name: "Tokyo Office", devices: 6, color: '#FED7AA' },
  ];

  const statusData = [
    { name: 'Active', value: 352, color: '#6366F1' },
    { name: 'Inactive', value: 26, color: '#FF8C66' },
    { name: 'Pending', value: 19, color: '#4ADE80' },
  ];

  const devicePlatformData = [
    { name: 'macOS', total: 124, active: 110, inactive: 8, pending: 6 },
    { name: 'Windows', total: 98, active: 85, inactive: 7, pending: 6 },
    { name: 'Linux', total: 45, active: 39, inactive: 4, pending: 2 },
    { name: 'iOS', total: 78, active: 72, inactive: 3, pending: 3 },
    { name: 'Android', total: 52, active: 46, inactive: 4, pending: 2 },
  ];

  const complianceData = [
    { name: "Security Compliance", value: 88 },
    { name: "Patching Compliance", value: 92 },
    { name: "Managed Devices", value: 96 },
  ];

  // Sample customer data for display
  const customerData = [
    { name: "Acme Corp", devices: 42 },
    { name: "Global Industries", devices: 36 },
    { name: "Stark Enterprises", devices: 28 },
    { name: "Wayne Technologies", devices: 23 },
    { name: "Umbrella Corporation", devices: 17 },
  ];

  return (
    <GlassCard variant="default" className="mt-6">
      <GlassCardHeader>
        <GlassCardTitle className="text-lg font-medium">Dashboard Data Tables</GlassCardTitle>
        <GlassCardDescription>Tabular representation of all dashboard metrics</GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent className="space-y-8">
        {/* Customer Distribution Table */}
        <div>
          <h3 className="text-md font-medium mb-2">Devices by Customer</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Device Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customerData.map((item) => (
                <TableRow key={`customer-${item.name}`}>
                  <TableCell className="font-medium">
                    <CustomerTag customer={item.name} />
                  </TableCell>
                  <TableCell className="text-right">{item.devices}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">
                  {customerData.reduce((sum, item) => sum + item.devices, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Devices by Department Table */}
        <div>
          <h3 className="text-md font-medium mb-2">Devices by Department</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Device Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {departmentData.map((item) => (
                <TableRow key={`dept-${item.name}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      {item.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{item.devices}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">
                  {departmentData.reduce((sum, item) => sum + item.devices, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Devices by Location Table */}
        <div>
          <h3 className="text-md font-medium mb-2">Devices by Location</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Device Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locationData.map((item) => (
                <TableRow key={`loc-${item.name}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      {item.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{item.devices}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">
                  {locationData.reduce((sum, item) => sum + item.devices, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Device Status Distribution Table */}
        <div>
          <h3 className="text-md font-medium mb-2">Device Status Distribution</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead className="text-right">Percentage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statusData.map((item) => {
                const total = statusData.reduce((sum, i) => sum + i.value, 0);
                const percentage = ((item.value / total) * 100).toFixed(1);
                
                return (
                  <TableRow key={`status-${item.name}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                        {item.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{item.value}</TableCell>
                    <TableCell className="text-right">{percentage}%</TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="font-medium">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">
                  {statusData.reduce((sum, item) => sum + item.value, 0)}
                </TableCell>
                <TableCell className="text-right">100%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Device Platform Breakdown Table */}
        <div>
          <h3 className="text-md font-medium mb-2">Device Platform Breakdown</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Platform</TableHead>
                <TableHead className="text-right">Active</TableHead>
                <TableHead className="text-right">Inactive</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devicePlatformData.map((item) => (
                <TableRow key={`platform-${item.name}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-right">{item.active}</TableCell>
                  <TableCell className="text-right">{item.inactive}</TableCell>
                  <TableCell className="text-right">{item.pending}</TableCell>
                  <TableCell className="text-right">{item.total}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">
                  {devicePlatformData.reduce((sum, item) => sum + item.active, 0)}
                </TableCell>
                <TableCell className="text-right">
                  {devicePlatformData.reduce((sum, item) => sum + item.inactive, 0)}
                </TableCell>
                <TableCell className="text-right">
                  {devicePlatformData.reduce((sum, item) => sum + item.pending, 0)}
                </TableCell>
                <TableCell className="text-right">
                  {devicePlatformData.reduce((sum, item) => sum + item.total, 0)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Compliance Metrics Table */}
        <div>
          <h3 className="text-md font-medium mb-2">Compliance Metrics</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Compliance Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {complianceData.map((item) => (
                <TableRow key={`compliance-${item.name}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-right">{item.value}%</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-medium">
                <TableCell>Average Compliance</TableCell>
                <TableCell className="text-right">
                  {(complianceData.reduce((sum, item) => sum + item.value, 0) / complianceData.length).toFixed(1)}%
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}
